/**
 * Consent Request Page
 *
 * React implementation of the consent request management page.
 * Handles listing, filtering, log inspection, downloads, and consent creation.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DrawerProvider } from 'equal-ds-ui';
import {
  ConsentFilters,
  ConsentTable,
  ConsentDrawer,
  CreateConsentDrawer,
  ConsentDownloadDrawer,
  FiDataDrawer,
  AccountDetailsDrawer,
} from '.';
import { CsvDownloadModal } from './CsvDownloadModal';
import { Toast } from '../../../components/ui/Toast';
import { useErrorHandler } from '../../../hooks/useErrorHandler';
import { envService } from '../../../services/env.service';
import { consentRequestAPI } from '../../../services/consent-request-api.service';
import { downloadAPI } from '../../../services/download-api.service';
import { accountAggregatorAPI } from '../../../services/account-aggregator-api.service';
import { productsAPI } from '../../../services/products-api.service';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PAGE_SIZE,
} from '../../../constants/pagination.constants';
import type {
  ConsentFiltersState,
  ConsentFilterOptions,
  ConsentRequestRecord,
  ConsentRequestPageProps,
  ConsentTableRow,
  DrawerTab,
  ConsentAccountGroup,
  ConsentAnalyticsOption,
  ConsentSearchDomain,
  ConsentTablePermissions,
  ConsentCreatePayload,
  ConsentCSVRow,
  ConsentAccountRecord,
  ConsentRequestListParams,
  ConsentTimelineAPIResponse,
  TransformedDataSession,
} from '../../../types/consent';
import type { TimelineEvent } from '../../../types/timeline';
import type { AggregatorOption, ConsentSuccessData } from './CreateConsentDrawer';
import { getAssistedJourneyFlags } from '../../../utils/assisted-journey';
import type { ProductRecord } from '../../../services/products-api.service';
import type { StateRecord } from '../../../types/consent';
import { transformConsentTimeline, transformDataSessions } from '../../../utils/timeline-transformers';
import { extractXmlPayload, wrapXmlIfNeeded } from '../../../utils/xml-utils';

interface UserInfo {
  role?: string;
  menuList?: Array<{
    code?: string;
    features?: Array<{ code?: string | null | undefined }>;
  }>;
  ui_config?: Record<string, any>;
}

interface ConsentFeatureFlags {
  canCreateConsent: boolean;
  canDownloadCsv: boolean;
}

const CONSENT_MENU_CODE = 'CONSENT-REQUEST';

// Static consent status options (known, finite values)
const CONSENT_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'active', label: 'ACTIVE' },
  { value: 'pending', label: 'PENDING' },
  { value: 'expired', label: 'EXPIRED' },
  { value: 'revoked', label: 'REVOKED' },
  { value: 'rejected', label: 'REJECTED' },
  { value: 'paused', label: 'PAUSED' },
];

const getUserInfo = (): UserInfo | null => {
  try {
    const encoded = sessionStorage.getItem('loggedUserInfo');
    if (!encoded) {
      return null;
    }
    return JSON.parse(atob(encoded));
  } catch (error) {
    console.error('[ConsentRequestPage] Failed to parse user info:', error);
    return null;
  }
};

const derivePermissions = (userInfo: UserInfo | null): {
  permissions: ConsentTablePermissions;
  featureFlags: ConsentFeatureFlags;
} => {
  if (!userInfo?.menuList) {
    return {
      permissions: {
        canViewFiData: true,
        canDownloadFiData: true,
        canViewLogs: true,
        canSendSms: false,
        canTriggerFiRequest: false,
        canViewAccountDetails: true,
      },
      featureFlags: {
        canCreateConsent: true,
        canDownloadCsv: true,
      },
    };
  }

  const menu = userInfo.menuList.find((item) => item.code === CONSENT_MENU_CODE);
  const features = menu?.features ?? [];
  const hasFeature = (code: string) =>
    features.some((feature) => feature.code?.trim() === code);
  const isSuperAdmin = userInfo.role === 'superadmin';

  return {
    permissions: {
      canViewFiData: hasFeature('CONSENTREQUESTVIEW') || isSuperAdmin,
      canDownloadFiData: hasFeature('CONSENTREQUEST-DOWNLOAD') || isSuperAdmin,
      canViewLogs: hasFeature('CONSENTREQUEST-VIEWLOGS') || isSuperAdmin,
      canSendSms: hasFeature('CONSENTREQUEST-SENDSMS') || isSuperAdmin,
      canTriggerFiRequest: hasFeature('CONSENTREQUEST-FIREQUEST') || isSuperAdmin,
      canViewAccountDetails: hasFeature('CONSENTREQUEST-ACCOUNTDETAILS') || isSuperAdmin,
    },
    featureFlags: {
      canCreateConsent: hasFeature('CREATECONSENTREQUEST') || isSuperAdmin,
      canDownloadCsv: hasFeature('CONSENTREQUESTDOWNLOADCSV') || isSuperAdmin,
    },
  };
};

const buildEmptyFilterOptions = (): ConsentFilterOptions => ({
  templates: [],
  purposeCodes: [],
  statuses: [],
  aggregators: [],
});

const buildInitialFilters = (): ConsentFiltersState => {
  return {
    selectedTemplate: null,
    selectedPurposeCode: null,
    selectedStatus: null,
    selectedAggregator: null,
  };
};

const normalizeString = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).toLowerCase().trim();
};

// Note: Client-side filtering removed - server-side filtering is now used
// The applyFilters and buildFilterOptions functions were removed as they are no longer needed

const groupAccountsByFip = (accounts: ConsentAccountRecord[]): ConsentAccountGroup[] => {
  const map = new Map<string, ConsentAccountGroup>();
  accounts.forEach((account) => {
    const fipId = account.fip_id ?? 'UNKNOWN';
    if (!map.has(fipId)) {
      map.set(fipId, { fip_id: fipId, accounts: [] });
    }
    map.get(fipId)?.accounts.push(account);
  });
  return Array.from(map.values());
};

const toTableRow = (record: ConsentRequestRecord, index: number): ConsentTableRow => {
  const rowId =
    record.consent_handle ??
    record.consent_id ??
    record.account_id ??
    `${record.customer_vua ?? 'row'}-${index}`;
  return {
    ...record,
    _rowId: rowId,
  };
};

export const ConsentRequestPage: React.FC<ConsentRequestPageProps> = () => {
  const envConfig = useMemo(() => envService.getConfig(), []);
  const userInfo = useMemo(() => getUserInfo(), []);

  const { errorMessage, handleError, clearError } = useErrorHandler();

  const { permissions, featureFlags } = useMemo(
    () => derivePermissions(userInfo),
    [userInfo]
  );

  const [rawRecords, setRawRecords] = useState<ConsentRequestRecord[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [smsEnabled, setSmsEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_CURRENT_PAGE);
  const [pageSize, setPageSize] = useState<number>(envConfig.pageSize ?? DEFAULT_PAGE_SIZE);
  const [searchState, setSearchState] = useState<{ domain: ConsentSearchDomain; query: string }>({
    domain: 'customer_vua',
    query: '',
  });

  const [filterOptions, setFilterOptions] = useState<ConsentFilterOptions>(buildEmptyFilterOptions());
  const [filters, setFilters] = useState<ConsentFiltersState>(buildInitialFilters());
  const [appliedFilters, setAppliedFilters] = useState<ConsentFiltersState>(buildInitialFilters());

  const hasUnappliedChanges = useMemo(() => {
    return JSON.stringify(filters) !== JSON.stringify(appliedFilters);
  }, [filters, appliedFilters]);

  const hasAnyFiltersSelected = useMemo(() => {
    return (
      filters.selectedTemplate !== null ||
      filters.selectedPurposeCode !== null ||
      filters.selectedStatus !== null ||
      filters.selectedAggregator !== null
    );
  }, [filters]);

  // Server-side filtering is now used - no client-side filtering needed
  // Backend returns filtered results, so we use rawRecords directly
  const tableRows = useMemo<ConsentTableRow[]>(
    () => rawRecords.map(toTableRow),
    [rawRecords]
  );

  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<DrawerTab>('consent');
  const [selectedConsent, setSelectedConsent] = useState<ConsentRequestRecord | null>(null);

  // Timeline state
  const [consentTimelineData, setConsentTimelineData] = useState<TimelineEvent[] | null>(null);
  const [dataSessionsData, setDataSessionsData] = useState<TransformedDataSession[] | null>(null);
  const [timelineMetadata, setTimelineMetadata] = useState<ConsentTimelineAPIResponse['timeline_metadata'] | null>(null);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [timelineLoading, setTimelineLoading] = useState<boolean>(false);

  // Cache for account details (used by download functionality)
  const accountCacheRef = useRef<Record<string, ConsentAccountGroup[]>>({});

  const [downloadDrawerOpen, setDownloadDrawerOpen] = useState<boolean>(false);
  const [downloadConsent, setDownloadConsent] = useState<ConsentRequestRecord | null>(null);
  const [downloadAccountGroups, setDownloadAccountGroups] = useState<ConsentAccountGroup[]>([]);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const [analyticsOptions, setAnalyticsOptions] = useState<ConsentAnalyticsOption[]>([]);
  const analyticsLoadedRef = useRef<boolean>(false);

  const [createDrawerOpen, setCreateDrawerOpen] = useState<boolean>(false);
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [aggregatorOptions, setAggregatorOptions] = useState<AggregatorOption[]>([]);
  const [productOptions, setProductOptions] = useState<ProductRecord[]>([]);
  const [productDivisions, setProductDivisions] = useState<string[]>([]);
  const [stateOptions, setStateOptions] = useState<StateRecord[]>([]);
  const [createSuccessMessage, setCreateSuccessMessage] = useState<string | null>(null);
  const [createErrorMessage, setCreateErrorMessage] = useState<string | null>(null);
  const createOptionsLoadedRef = useRef<boolean>(false);

  // FI Data Drawer state
  const [fiDataDrawerOpen, setFiDataDrawerOpen] = useState<boolean>(false);
  const [fiDataConsent, setFiDataConsent] = useState<ConsentRequestRecord | null>(null);

  // Account Details Drawer state
  const [accountDetailsDrawerOpen, setAccountDetailsDrawerOpen] = useState<boolean>(false);
  const [accountDetailsConsent, setAccountDetailsConsent] = useState<ConsentRequestRecord | null>(null);

  // CSV Download Modal state
  const [csvModalOpen, setCsvModalOpen] = useState<boolean>(false);
  const [csvDownloading, setCsvDownloading] = useState<boolean>(false);

  // Consent Success Data (for showing success view in CreateConsentDrawer)
  const [consentSuccessData, setConsentSuccessData] = useState<ConsentSuccessData | null>(null);

  const [globalSuccess, setGlobalSuccess] = useState<string | null>(null);

  const endUserListEnabled = Boolean(envConfig.endUserList);
  const userUiConfig = (userInfo as any)?.ui_config ?? {};
  const allowAdditionalInfo = Boolean(userUiConfig?.CreateConsent?.additionalnfoSec);
  const allowStateSelection = Boolean(userUiConfig?.common?.state_config?.addconsent_form);
  const multiConsentLimit = useMemo(() => {
    const fromSession = sessionStorage.getItem('multiConsentLimit');
    if (fromSession) {
      const parsed = Number(fromSession);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return 1;
  }, []);
  const allowMultiConsent =
    sessionStorage.getItem('featureFlagMultiConsentV3') === 'true';

  // Assisted journey flags - read once on mount (flags are set during app init and don't change)
  const assistedJourneyFlags = useMemo(() => getAssistedJourneyFlags(), []);

  const loadConsentRequests = useCallback(async () => {
    setIsLoading(true);
    clearError();

    try {
      const payload: ConsentRequestListParams = {
        limit: pageSize,
        pageno: currentPage,
      };

      // Add search query to payload
      const trimmedQuery = searchState.query.trim();
      if (trimmedQuery) {
        switch (searchState.domain) {
          case 'customer_vua':
            payload.customer_vua = trimmedQuery;
            break;
          case 'consent_id':
            payload.consent_id = trimmedQuery;
            break;
          case 'consent_handle':
            payload.consent_handle = trimmedQuery;
            break;
          case 'account_id':
            payload.account_id = trimmedQuery;
            break;
          case 'mobile_no':
            payload.mobile_no = trimmedQuery;
            break;
          case 'consent_status':
            payload.consent_status = trimmedQuery;
            break;
          case 'data_delivered':
            payload.data_delivered = trimmedQuery;
            break;
        }
      }

      // Add filter params to payload (server-side filtering)
      if (appliedFilters.selectedStatus) {
        // Backend expects uppercase status values
        payload.status = appliedFilters.selectedStatus.toUpperCase();
      }
      if (appliedFilters.selectedTemplate) {
        payload.product_id = appliedFilters.selectedTemplate;
      }
      if (appliedFilters.selectedPurposeCode) {
        payload.purpose_code = appliedFilters.selectedPurposeCode;
      }
      if (appliedFilters.selectedAggregator) {
        payload.aa_id = appliedFilters.selectedAggregator;
      }

      const response = await consentRequestAPI.getConsentRequests(payload);
      const rows = response.data.rows || [];
      setRawRecords(rows);
      setTotalCount(response.data.count ?? rows.length);
      setSmsEnabled(Boolean(response.data.SmsNotificationStatus));
    } catch (error) {
      handleError(error, { component: 'ConsentRequestPage', action: 'fetchConsentRequests' });
      setRawRecords([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [pageSize, currentPage, searchState, appliedFilters, handleError, clearError]);

  useEffect(() => {
    void loadConsentRequests();
  }, [loadConsentRequests]);

  // Fetch master data for filter dropdowns on page load
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const [productsRes, purposesRes, aggregatorsRes] = await Promise.all([
          consentRequestAPI.getProductsList(),
          consentRequestAPI.getPurposesList(),
          consentRequestAPI.getAggregatorsList(),
        ]);

        // Extract rows from nested response structure: { data: { rows: [...] } }
        const productsRows = productsRes?.data?.rows || [];
        const purposesRows = purposesRes?.data?.rows || [];
        const aggregatorsRows = aggregatorsRes?.data?.rows || [];

        const templates = productsRows.map((p) => ({
          value: normalizeString(p.product_code),
          label: p.product_name || p.product_code,
        }));

        const purposeCodes = purposesRows.map((p) => ({
          value: normalizeString(p.purpose_code),
          label: p.purpose_text && p.purpose_code
            ? `${p.purpose_text} (${p.purpose_code})`
            : p.purpose_text || p.purpose_code,
        }));

        const aggregators = aggregatorsRows.map((a) => ({
          value: normalizeString(a.account_aggregator_id),
          label: a.account_aggregator_name || a.account_aggregator_id,
        }));

        setFilterOptions({
          templates,
          purposeCodes,
          statuses: CONSENT_STATUS_OPTIONS,
          aggregators,
        });
      } catch (error) {
        console.warn('[ConsentRequestPage] Failed to load master data for filters:', error);
        // Fallback to static statuses at minimum
        setFilterOptions((prev) => ({
          ...prev,
          statuses: CONSENT_STATUS_OPTIONS,
        }));
      }
    };

    void loadMasterData();
  }, []);

  const handleFiltersChange = useCallback((changes: Partial<ConsentFiltersState>) => {
    setFilters((prev) => ({
      ...prev,
      ...changes,
    }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setAppliedFilters(filters);
    setCurrentPage(DEFAULT_CURRENT_PAGE); // Reset to page 1 when filters are applied
  }, [filters]);

  const handleResetFilters = useCallback(() => {
    const initial = buildInitialFilters();
    setFilters(initial);
    setAppliedFilters(initial);
    setCurrentPage(DEFAULT_CURRENT_PAGE); // Reset to page 1 when filters are reset
  }, []);

  const handleSearch = useCallback((domain: ConsentSearchDomain, query: string) => {
    setCurrentPage(DEFAULT_CURRENT_PAGE);
    setSearchState({ domain, query });
  }, []);

  const ensureAccountDetails = useCallback(
    async (consent: ConsentRequestRecord, forceReload = false): Promise<ConsentAccountGroup[]> => {
      const cacheKey = consent.consent_id ?? consent.consent_handle ?? '';
      if (!forceReload && cacheKey && accountCacheRef.current[cacheKey]) {
        return accountCacheRef.current[cacheKey];
      }

      const consentId = consent.consent_id;
      if (!consentId) {
        return [];
      }

      try {
        const response = await consentRequestAPI.getConsentAccountDetails({ consentId });
        const rows = response.data.artefactAccounts?.rows ?? [];
        const groups = groupAccountsByFip(rows);
        if (cacheKey) {
          accountCacheRef.current[cacheKey] = groups;
        }
        return groups;
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'fetchAccountDetails' });
        return [];
      }
    },
    [handleError]
  );

  const ensureAnalyticsOptions = useCallback(async () => {
    if (analyticsLoadedRef.current) {
      return;
    }
    try {
      const response = await consentRequestAPI.getAnalyticsOptions();
      setAnalyticsOptions(response.data ?? []);
      analyticsLoadedRef.current = true;
    } catch (error) {
      console.warn('[ConsentRequestPage] Unable to load analytics options', error);
    }
  }, []);

  const handleViewLogs = useCallback(
    async (row: ConsentRequestRecord, tab: DrawerTab) => {
      setSelectedConsent(row);
      setActiveTab(tab);
      setDrawerOpen(true);

      // Reset timeline state
      setConsentTimelineData(null);
      setDataSessionsData(null);
      setTimelineMetadata(null);
      setTimelineError(null);
      setTimelineLoading(true); // Start loading

      if (!row.consent_handle) {
        setTimelineError('Invalid consent handle');
        setTimelineLoading(false);
        return;
      }

      try {
        // Call consolidated timeline API
        const response = await consentRequestAPI.getTimelineV2({
          consent_handle: row.consent_handle,
        });

        // Transform API data using transformers
        const consentTimeline = transformConsentTimeline(response.consent_timeline);
        const dataSessions = transformDataSessions(response.data_timeline.sessions);

        // Store transformed data
        setConsentTimelineData(consentTimeline);
        setDataSessionsData(dataSessions);
        setTimelineMetadata(response.timeline_metadata);

      } catch (error) {
        console.error('[ConsentRequestPage] Timeline API Error:', error);
        handleError(error, { component: 'ConsentRequestPage', action: 'fetchTimeline' });

        // Set error state - drawer will show error message
        setTimelineError('Failed to load timeline data. Please try again.');
        setConsentTimelineData(null);
        setDataSessionsData(null);
        setTimelineMetadata(null);
      } finally {
        setTimelineLoading(false); // Stop loading
      }
    },
    [handleError]
  );

  const handleRefreshConsent = useCallback(
    async (row: ConsentRequestRecord) => {
      if (!row.consent_handle) {
        return;
      }
      try {
        await consentRequestAPI.refreshConsent({ ConsentHandle: [row.consent_handle] });
        setGlobalSuccess(`Latest consent details fetched for ${row.customer_vua ?? row.account_id ?? ''}`);
        void loadConsentRequests();
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'refreshConsent' });
      }
    },
    [handleError, loadConsentRequests]
  );

  const handleResendSms = useCallback(
    async (row: ConsentRequestRecord) => {
      if (!row.consent_handle) {
        return;
      }
      try {
        await consentRequestAPI.resendSms({ consenthandle: row.consent_handle });
        setGlobalSuccess('SMS notification has been queued successfully.');
        void loadConsentRequests();
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'resendSms' });
      }
    },
    [handleError, loadConsentRequests]
  );

  const handleTriggerFiRequest = useCallback(
    async (row: ConsentRequestRecord) => {
      if (!row.consent_id) {
        return;
      }
      try {
        await consentRequestAPI.triggerFiRequest({ consentID: row.consent_id });
        setGlobalSuccess('FI request placed successfully.');
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'triggerFiRequest' });
      }
    },
    [handleError]
  );

  const handleOpenDownload = useCallback(
    async (row: ConsentRequestRecord) => {
      setDownloadConsent(row);
      const groups = await ensureAccountDetails(row);
      setDownloadAccountGroups(groups);
      await ensureAnalyticsOptions();
      setDownloadDrawerOpen(true);
    },
    [ensureAccountDetails, ensureAnalyticsOptions]
  );

  const handleViewFiData = useCallback(
    async (row: ConsentRequestRecord) => {
      await ensureAnalyticsOptions();
      setFiDataConsent(row);
      setFiDataDrawerOpen(true);
    },
    [ensureAnalyticsOptions]
  );

  const handleViewAccountDetails = useCallback((row: ConsentRequestRecord) => {
    setAccountDetailsConsent(row);
    setAccountDetailsDrawerOpen(true);
  }, []);

const downloadTextFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(link.href);
};

const downloadJsonFile = (data: unknown, filename: string) => {
  downloadTextFile(JSON.stringify(data, null, 2), filename, 'application/json');
};

const downloadBlob = (blob: Blob, filename: string) => {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(link.href);
};

const downloadCsv = (rows: ConsentCSVRow[], filename: string) => {
  if (!rows || rows.length === 0) {
    downloadTextFile('No data available', filename, 'text/csv');
    return;
  }
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(','),
    ...rows.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          if (value === null || value === undefined) {
            return '';
          }
          const stringValue = String(value).replace(/"/g, '""');
          return `"${stringValue}"`;
        })
        .join(',')
    ),
  ].join('\r\n');
  downloadTextFile(csv, filename, 'text/csv');
};

  const handleDownloadFiData = useCallback(
    async (format: 'JSON' | 'PDF' | 'XML') => {
      if (!downloadConsent) {
        return;
      }
      setDownloadLoading(true);
      try {
        const consentId = downloadConsent.consent_id ?? '';
        if (format === 'JSON') {
          const response = await consentRequestAPI.downloadFiDataJson({
            consentID: consentId,
            downloadjson: true,
          });
          downloadJsonFile(response.data ?? response, `FI-Statement-${consentId}.json`);
        } else if (format === 'PDF') {
          const linkRefNumbers = downloadAccountGroups.flatMap((group) =>
            group.accounts
              .map((account) => account.link_reference_number)
              .filter(Boolean) as string[]
          );
          const response = await consentRequestAPI.downloadFiDataPdf({
            consentID: consentId,
            linkRefNumber: linkRefNumbers,
          });
          downloadBlob(response, `FI-Statement-${consentId}.pdf`);
        } else if (format === 'XML') {
          const response = await consentRequestAPI.downloadFiDataXml({
            consentID: consentId,
          });
          const xmlPayload = extractXmlPayload(response);
          const wrappedXml = wrapXmlIfNeeded(xmlPayload);
          downloadTextFile(wrappedXml, `FI-Statement-${consentId}.xml`, 'application/xml');
        }
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'downloadFiData' });
      } finally {
        setDownloadLoading(false);
      }
    },
    [downloadConsent, downloadAccountGroups, handleError]
  );

  const handleDownloadAnalytics = useCallback(
    async (option: ConsentAnalyticsOption, format: 'JSON' | 'EXCEL', linkRefNumber: string) => {
      if (!downloadConsent?.consent_id && !fiDataConsent?.consent_id) {
        return;
      }
      const consentId = downloadConsent?.consent_id ?? fiDataConsent?.consent_id ?? '';
      setDownloadLoading(true);
      try {
        const response = await consentRequestAPI.downloadAnalytics({
          consentID: consentId,
          linkRefNumber: linkRefNumber,
          analyticsType: option.name,
          analyticsVersion: option.version,
          analyticsFormat: format,
        });
        if (format === 'JSON') {
          downloadJsonFile(response.data ?? response, `Analytics-${option.name}-${consentId}.json`);
        } else {
          downloadBlob(
            response,
            `Analytics-${option.name}-${consentId}.${format === 'EXCEL' ? 'xlsx' : 'bin'}`
          );
        }
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'downloadAnalytics' });
      } finally {
        setDownloadLoading(false);
      }
    },
    [downloadConsent, fiDataConsent, handleError]
  );

  const loadCreateConsentOptions = useCallback(async () => {
    if (createOptionsLoadedRef.current) {
      return;
    }
    try {
      const [aggregatorResponse, productsResponse, statesResponse] = await Promise.all([
        accountAggregatorAPI.listAllAggregators(),
        productsAPI.getProducts(''),
        consentRequestAPI.getStates(),
      ]);

      const aggregators = (aggregatorResponse.data.rows ?? [])
        .filter((aggregator) => {
          const status = normalizeString(aggregator.status ? String(aggregator.status) : 'ACTIVE');
          const channelStatus = normalizeString(
            aggregator.channel_status ? String(aggregator.channel_status) : 'ACTIVE'
          );
          return status === 'active' && channelStatus !== 'inactive';
        })
        .map((aggregator) => ({
          value: aggregator.aa_handle,
          label: aggregator.account_aggregator_name ?? aggregator.aa_handle,
          logoUrl: aggregator.logo_url ?? undefined,
        }));

      const products = (productsResponse.data.rows ?? []).filter(
        (product) => normalizeString(product.status) === 'active'
      );

      const divisions = Array.from(
        new Set(
          products
            .map((product) => product.product_division)
            .filter((division): division is string => Boolean(division))
        )
      );

      const states = statesResponse.data.rows ?? [];

      setAggregatorOptions(aggregators);
      setProductOptions(products);
      setProductDivisions(divisions);
      setStateOptions(states);
      createOptionsLoadedRef.current = true;
    } catch (error) {
      handleError(error, { component: 'ConsentRequestPage', action: 'loadCreateConsentOptions' });
    }
  }, [handleError]);

  const handleOpenCreateDrawer = useCallback(async () => {
    setCreateSuccessMessage(null);
    setCreateErrorMessage(null);
    await loadCreateConsentOptions();
    setCreateDrawerOpen(true);
  }, [loadCreateConsentOptions]);

  const handleFetchEndUserDetails = useCallback(
    async (mobileNumber: string) => {
      if (!endUserListEnabled) {
        return null;
      }
      try {
        const response = await consentRequestAPI.getEndUserDetails({ mobileNumber });
        return response.data?.rawData as any;
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'fetchEndUserDetails' });
        return null;
      }
    },
    [endUserListEnabled, handleError]
  );

  const handleCreateConsentSubmit = useCallback(
    async (payload: ConsentCreatePayload) => {
      setCreateLoading(true);
      setCreateSuccessMessage(null);
      setCreateErrorMessage(null);
      try {
        let response;
        if (endUserListEnabled) {
          response = await consentRequestAPI.createConsentForEndUser({
            ...payload,
            request_type: 'V3',
          });
        } else if (payload.accountAggregator === '@onemoney') {
          response = await consentRequestAPI.createConsentV2(payload);
        } else {
          response = await consentRequestAPI.createConsent(payload);
        }

        const message =
          response.message ??
          (payload.accountAggregator === '@onemoney'
            ? 'Consent requested successfully. Please advise the customer to approve the request on Onemoney.'
            : 'Consent requested successfully.');

        setCreateSuccessMessage(message);
        setGlobalSuccess(message);
        void loadConsentRequests();

        const redirectionUrl = response.data?.webRedirectionUrl;

        if (redirectionUrl) {
          if (assistedJourneyFlags.enabled) {
            // Show success view with QR code and sharing options in the same drawer
            setConsentSuccessData({
              consentLink: redirectionUrl,
              consentHandle: response.data?.consent_handle || '',
              mobileNumber: payload.partyIdentifierValue || '',
            });
            // Keep the drawer open - it will show the success view
          } else {
            // Use old redirect behavior when assisted journey is disabled
            setCreateDrawerOpen(false);
            if (envConfig.redirectionNewTab) {
              const win = window.open('', '_blank', 'noopener,noreferrer');
              if (win) {
                setTimeout(() => {
                  win.location.href = redirectionUrl;
                }, 1500);
              }
            } else if (envConfig.redirection) {
              window.location.href = redirectionUrl;
            }
          }
        } else {
          // No redirection URL - just close create drawer
          setCreateDrawerOpen(false);
        }
      } catch (error) {
        setCreateErrorMessage('Failed to create consent request. Please try again.');
        handleError(error, { component: 'ConsentRequestPage', action: 'createConsent' });
      } finally {
        setCreateLoading(false);
      }
    },
    [assistedJourneyFlags.enabled, endUserListEnabled, envConfig.redirection, envConfig.redirectionNewTab, handleError, loadConsentRequests]
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  }, []);

  /**
   * Reset consent success data when closing the success view
   */
  const handleResetConsentSuccess = useCallback(() => {
    setConsentSuccessData(null);
    setCreateSuccessMessage(null);
  }, []);

  const isJobBasedDownloadEnabled = sessionStorage.getItem('isJobBasedDownloadEnabled') === 'true';

  const handleCsvDownload = useCallback(
    async (startDate: Date, endDate: Date) => {
      const jobBasedEnabled = sessionStorage.getItem('isJobBasedDownloadEnabled') === 'true';
      setCsvDownloading(true);
      try {
        // Fix: Format dates to YYYY-MM-DD without timezone conversion
        // Setting to noon avoids timezone shift issues when converting to ISO string
        const startDateFixed = new Date(startDate);
        startDateFixed.setHours(12, 0, 0, 0);
        const endDateFixed = new Date(endDate);
        endDateFixed.setHours(12, 0, 0, 0);

        const fromDate = startDateFixed.toISOString().split('T')[0];
        const toDate = endDateFixed.toISOString().split('T')[0];

        if (jobBasedEnabled) {
          await downloadAPI.createJob({
            download_type: 'CONSENT_LIST',
            file_type: 'EXCEL',
            filters: { from: fromDate, to: toDate },
          });
          setCsvModalOpen(false);
          setGlobalSuccess('Report queued! Check the Download Centre');
        } else {
          const response = await consentRequestAPI.getConsentRequestsByDate({
            from: fromDate,
            to: toDate,
          });

          downloadCsv(response.data.rows ?? [], 'consent-requests.csv');
          setCsvModalOpen(false);
          setGlobalSuccess('CSV downloaded successfully');
        }
      } catch (error) {
        handleError(error, { component: 'ConsentRequestPage', action: 'downloadCsv' });
      } finally {
        setCsvDownloading(false);
      }
    },
    [handleError]
  );


  return (
    <DrawerProvider side="right" open={drawerOpen} onOpenChange={setDrawerOpen}>
      <div className="h-full flex flex-col p-4 space-y-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary tracking-wide">
            Consent Management
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Monitor consent requests, review logs, download FI information, and initiate new requests.
          </p>
        </div>

        {/* Fixed position toasts for messages - visible regardless of scroll position */}
        {errorMessage ? (
          <Toast
            message={errorMessage}
            variant="error"
            duration={5000}
            onClose={clearError}
          />
        ) : null}

        {globalSuccess ? (
          <Toast
            message={globalSuccess}
            variant="success"
            duration={4000}
            onClose={() => setGlobalSuccess(null)}
          />
        ) : null}

        <ConsentFilters
          filterOptions={filterOptions}
          selectedFilters={filters}
          onFiltersChange={handleFiltersChange}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          hasUnappliedChanges={hasUnappliedChanges}
          hasAnyFiltersSelected={hasAnyFiltersSelected}
        />

        <ConsentTable
          data={tableRows}
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={totalCount}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onViewLogs={handleViewLogs}
          onRefreshRow={handleRefreshConsent}
          onRefreshPage={loadConsentRequests}
          onOpenDownloadOptions={handleOpenDownload}
          onViewFiData={permissions.canViewFiData ? handleViewFiData : undefined}
          onViewAccountDetails={permissions.canViewAccountDetails ? handleViewAccountDetails : undefined}
          onTriggerFiRequest={permissions.canTriggerFiRequest ? handleTriggerFiRequest : undefined}
          onSendSms={permissions.canSendSms ? handleResendSms : undefined}
          onSearch={handleSearch}
          currentSearch={searchState}
          isLoading={isLoading}
          permissions={permissions}
          smsEnabled={smsEnabled}
          onCreateConsent={featureFlags.canCreateConsent ? handleOpenCreateDrawer : undefined}
          onDownloadCsv={
            featureFlags.canDownloadCsv
              ? () => setCsvModalOpen(true)
              : undefined
          }
        />
      </div>

      <ConsentDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        selectedConsent={selectedConsent}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        consentTimeline={consentTimelineData}
        dataSessions={dataSessionsData}
        metadata={timelineMetadata}
        error={timelineError}
        isLoading={timelineLoading}
      />

      <DrawerProvider
        side="right"
        open={downloadDrawerOpen}
        onOpenChange={setDownloadDrawerOpen}
      >
        <ConsentDownloadDrawer
          isOpen={downloadDrawerOpen}
          onClose={() => setDownloadDrawerOpen(false)}
          consent={downloadConsent}
          accountGroups={downloadAccountGroups}
          analyticsOptions={analyticsOptions}
          isDownloading={downloadLoading}
          onDownloadFiData={handleDownloadFiData}
          onDownloadAnalytics={handleDownloadAnalytics}
        />
      </DrawerProvider>

      <DrawerProvider
        side="right"
        open={createDrawerOpen}
        onOpenChange={setCreateDrawerOpen}
      >
        <CreateConsentDrawer
          isOpen={createDrawerOpen}
          onClose={() => {
            setCreateDrawerOpen(false);
            handleResetConsentSuccess();
          }}
          onSubmit={handleCreateConsentSubmit}
          isSubmitting={createLoading}
          aggregators={aggregatorOptions}
          products={productOptions}
          productDivisions={productDivisions}
          states={stateOptions}
          multiConsentLimit={multiConsentLimit}
          allowMultiConsent={allowMultiConsent}
          allowAdditionalInfo={allowAdditionalInfo}
          allowStateSelection={allowStateSelection}
          endUserListEnabled={endUserListEnabled}
          defaultAggregator={aggregatorOptions.find((agg) => agg.value === '@onemoney')?.value}
          onFetchEndUserDetails={endUserListEnabled ? handleFetchEndUserDetails : undefined}
          onGenerateAccountId={() => `ACC-${Date.now()}`}
          successMessage={createSuccessMessage}
          errorMessage={createErrorMessage}
          consentSuccessData={consentSuccessData}
          onResetSuccess={handleResetConsentSuccess}
        />
      </DrawerProvider>

      <DrawerProvider
        side="right"
        open={fiDataDrawerOpen}
        onOpenChange={setFiDataDrawerOpen}
      >
        <FiDataDrawer
          isOpen={fiDataDrawerOpen}
          onClose={() => setFiDataDrawerOpen(false)}
          consent={fiDataConsent}
          analyticsOptions={analyticsOptions}
          onDownloadAnalytics={handleDownloadAnalytics}
        />
      </DrawerProvider>

      <DrawerProvider
        side="right"
        open={accountDetailsDrawerOpen}
        onOpenChange={setAccountDetailsDrawerOpen}
      >
        <AccountDetailsDrawer
          isOpen={accountDetailsDrawerOpen}
          onClose={() => setAccountDetailsDrawerOpen(false)}
          consent={accountDetailsConsent}
        />
      </DrawerProvider>

      {/* CSV Download Modal */}
      <DrawerProvider
        side="right"
        open={csvModalOpen}
        onOpenChange={setCsvModalOpen}
      >
        {csvModalOpen && (
          <CsvDownloadModal
            onClose={() => setCsvModalOpen(false)}
            onDownload={handleCsvDownload}
            isDownloading={csvDownloading}
            isJobBasedDownload={isJobBasedDownloadEnabled}
          />
        )}
      </DrawerProvider>
    </DrawerProvider>
  );
};
